import { useEffect, useRef, useState } from "react";
import { applyStyleExtras, watchVectorFailure } from "./mapFallback";
import { loadMapLibre } from "./mapLoader";
import { ATTRIBUTION, DEFAULT_ZOOM, MAP_STYLES, SATELLITE_STYLE } from "./mapConfig";
import { circleGeoJson } from "./geoCircle";

// خريطة MapLibre: مؤشر قابل للسحب + دائرة نطاق + تراجع تلقائي لبلاطات صور عند فشل المتجهة.
export const useMapLibre = ({ container, center, radiusM, onMove, lang, theme, satellite }) => {
  const map = useRef(null);
  const marker = useRef(null);
  const raster = useRef(false);
  const latest = useRef({ center, radiusM, lang, onMove, satellite });
  latest.current = { center, radiusM, lang, onMove, satellite };
  const [error, setError] = useState(null);

  useEffect(() => {
    let cancelled = false;
    let stopWatch = null;
    loadMapLibre()
      .then((gl) => {
        if (cancelled || !container.current || map.current) return;
        const start = latest.current.center;
        map.current = new gl.Map({
          container: container.current,
          style: MAP_STYLES[theme] || MAP_STYLES.light,
          center: [start.longitude, start.latitude],
          zoom: DEFAULT_ZOOM,
          attributionControl: { compact: true, customAttribution: ATTRIBUTION },
        });
        map.current.on("error", (e) => console.error("maplibre", e?.error?.message || e));
        map.current.addControl(new gl.NavigationControl({ showCompass: false }), "top-left");
        map.current.addControl(new gl.GeolocateControl({ trackUserLocation: false }), "top-left");
        marker.current = new gl.Marker({ color: "#22c55e", draggable: true })
          .setLngLat([start.longitude, start.latitude])
          .addTo(map.current);
        marker.current.on("dragend", () => {
          const { lat, lng } = marker.current.getLngLat();
          latest.current.onMove({ latitude: lat, longitude: lng });
        });
        map.current.on("click", (e) => {
          if (e.originalEvent?.target?.closest?.(".maplibregl-marker")) return;
          latest.current.onMove({ latitude: e.lngLat.lat, longitude: e.lngLat.lng });
        });
        map.current.on("style.load", () => {
          const s = latest.current;
          applyStyleExtras(map.current, { ...s, raster: raster.current || latest.current.satellite });
        });
        stopWatch = watchVectorFailure(map.current, () => {
          raster.current = true;
        });
      })
      .catch((err) => setError(err.message));
    return () => {
      cancelled = true;
      if (stopWatch) stopWatch();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!map.current || !marker.current) return;
    marker.current.setLngLat([center.longitude, center.latitude]);
    map.current.easeTo({ center: [center.longitude, center.latitude], duration: 500 });
    const source = map.current.getSource("geofence");
    if (source) source.setData(circleGeoJson(center.latitude, center.longitude, radiusM));
  }, [center.latitude, center.longitude, radiusM]);

  useEffect(() => {
    if (!map.current) return;
    map.current.setStyle(satellite ? SATELLITE_STYLE : (MAP_STYLES[theme] || MAP_STYLES.light));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [satellite]);

  useEffect(() => {
    if (!map.current) return;
    map.current.getContainer().dir = "ltr";
    if (map.current.isStyleLoaded() && !raster.current) {
      applyStyleExtras(map.current, { ...latest.current, raster: false });
    }
  }, [lang]);

  return { map, error };
};
